import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { SubscriptionApi } from 'modules/subscription/api';
import logo from 'modules/ui/assets/images/logo.png';
import { Button } from 'modules/ui/components';
import { Colors } from 'modules/ui/theme/colors';
import { Routes } from 'Routes';
import { AuthImageContainer } from '../../../auth/components';
import {
  PageContainer,
  LogoImage,
  Title,
  Subtitle,
  ToggleContainer,
  ToggleLabel,
  CardsContainer,
  PlanCard,
  PlanHeader,
  PlanName,
  SelectedBadge,
  PlanPrice,
  PriceAmount,
  PricePeriod,
  YearlySavings,
  FeaturesList,
  FeatureItem,
  CheckIcon,
  CardError,
} from './styledComponents';

type Plan = 'pro' | 'unlimited';
type BillingCycle = 'monthly' | 'yearly';

const PLANS = {
  pro: {
    name: 'Pro',
    features: [
      'Ensemble routing (3 per day)',
      'Polar spread analysis',
      'All weather data layers',
      'Standard support',
    ],
    prices: {
      monthly: 29,
      yearly: 290,
    },
  },
  unlimited: {
    name: 'Unlimited',
    features: [
      'Unlimited ensemble routing',
      'Unlimited polar spread analysis',
      'All weather data layers',
      'Priority support',
      'All future features included',
    ],
    prices: {
      monthly: 49,
      yearly: 490,
    },
  },
} as const;

const TogglePillBase = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: ${({ $active }): string =>
    $active ? Colors.Primary : '#E8EAF1'};
  border-radius: 12px;
  position: relative;
  transition: background-color 0.25s linear;

  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: #ffffff;
    top: 3px;
    left: ${({ $active }): string => ($active ? '23px' : '3px')};
    transition: left 0.25s ease-in-out;
  }
`;

const TogglePill = React.memo(TogglePillBase);

const SaveBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.Secondary};
  margin-left: 4px;
`;

const UpgradeForm: React.FC = () => {
  const history = useHistory();
  const [selectedPlan, setSelectedPlan] = useState<Plan>('pro');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = useCallback(
    (checked: boolean) => setBillingCycle(checked ? 'yearly' : 'monthly'),
    [],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { url } = await SubscriptionApi.checkout({
        plan: selectedPlan,
        period: billingCycle,
      });

      window.open(url, '_blank');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPlan, billingCycle]);

  const isYearly = billingCycle === 'yearly';

  return (
    <AuthImageContainer>
      <PageContainer>
        <LogoImage src={logo} alt="Dorado sail" />
        <Title>Choose your plan</Title>
        <Subtitle>
          Upgrade to unlock advanced routing and weather features.
        </Subtitle>

        <ToggleContainer>
          <ToggleLabel $active={!isYearly}>Monthly</ToggleLabel>
          <label style={{ cursor: 'pointer', display: 'flex' }}>
            <input
              type="checkbox"
              checked={isYearly}
              onChange={(e): void => handleToggle(e.target.checked)}
              style={{ display: 'none' }}
            />
            <TogglePill $active={isYearly} />
          </label>
          <ToggleLabel $active={isYearly}>
            Yearly <SaveBadge>Save ~17%</SaveBadge>
          </ToggleLabel>
        </ToggleContainer>

        <CardsContainer>
          {(Object.keys(PLANS) as Plan[]).map((planKey) => {
            const plan = PLANS[planKey];
            const isSelected = selectedPlan === planKey;
            const price = plan.prices[billingCycle];

            return (
              <PlanCard
                key={planKey}
                $selected={isSelected}
                onClick={(): void => setSelectedPlan(planKey)}
              >
                <PlanHeader>
                  <PlanName>{plan.name}</PlanName>
                  {isSelected && <SelectedBadge>Selected</SelectedBadge>}
                </PlanHeader>
                <PlanPrice>
                  <PriceAmount>${price}</PriceAmount>
                  <PricePeriod>/{isYearly ? 'year' : 'month'}</PricePeriod>
                  {isYearly && (
                    <YearlySavings>
                      ${Math.round((plan.prices.monthly * 12 - price) / 12)}
                      /mo savings
                    </YearlySavings>
                  )}
                </PlanPrice>
                <FeaturesList>
                  {plan.features.map((feature) => (
                    <FeatureItem key={feature}>
                      <CheckIcon>&#10003;</CheckIcon>
                      {feature}
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </PlanCard>
            );
          })}
        </CardsContainer>

        {error && <CardError>{error}</CardError>}

        <Button
          label="Subscribe"
          clickHandler={handleSubmit}
          isLoading={loading}
          style={{ width: 200 }}
        />
        <Button
          label="Cancel"
          clickHandler={(): void => history.push(Routes.App)}
          isSecondary
          style={{ width: 200, marginTop: 12 }}
        />
      </PageContainer>
    </AuthImageContainer>
  );
};

export default React.memo(UpgradeForm);
